import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useToast } from 'use-toast-mui';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import moment from 'moment';
import { deleteUserById, updateUserById } from '../../../api/user';
import { converterJsonToFormData, errorMessages, removeEmptyValues } from '../../../utils/methods';
import useUserAuth from '../../../utils/useUserAuth';
import BannerDeleteModal from '../../banner/BannerDeleteModal';
import UserDefaultModal from '../userTabModal/UserDefaultModal/UserDefaultModal';
import GuardianRegisterModal from '../userTabModal/GuardianRegisterModal';
import PointChangeModal from '../UserPointHistory/PointChangeModal';
import { PageContainer } from './UserGeneralTabStyled';
import UserProfileSection from './UserProfileSection';
import UserUsageTables from './UserUsageTables';

const UserGeneralTab = props => {
  const toast = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberId = props.user.memberId;
  // userInfo / userBranchId는 부모(UserDetail)에서 lifting 된 상태 — 헤더 카드와 공유.
  const { userInfo, userBranchId, setUserBranchId } = props;
  const user = useSelector(state => state.user.user);
  const canInfoEdit = useUserAuth('MEMBER_INFO_EDIT');
  const { handleSubmit, control, reset, setError, setValue, watch } = useForm({
    defaultValues: props.user,
  });
  const [previewImage] = useState(props.user?.authImage);
  const [address, setAddress] = useState('');
  const [userTrainerId, setUserTrainerId] = useState();
  const [opens, setOpens] = useState(false);
  const [openDefaultModal, setOpenDefaultModal] = useState(false);
  const [openGuardianModal, setOpenGuardianModal] = useState(false);
  const [openPointModal, setOpenPointModal] = useState(false);

  const watchedFields = watch();

  const scriptUrl = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

  const open = useDaumPostcodePopup(scriptUrl);

  const handleComplete = data => {
    let fullAddress = data.address;
    let extraAddress = '';

    if (data.addressType === 'R') {
      if (data.bname !== '') {
        extraAddress += data.bname;
      }
      if (data.buildingName !== '') {
        extraAddress += extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName;
      }
      fullAddress += extraAddress !== '' ? ` (${extraAddress})` : '';
    }
    setAddress(fullAddress);
    setValue('address', fullAddress);
  };

  const handleClick = () => {
    open({ onComplete: handleComplete });
  };

  // 지점 변경 시 form 값만 동기화 — userInfo fetch는 부모(UserDetail)의 useEffect가 처리.
  const onPointChange = async e => {
    setUserBranchId(e.target.value);
    setValue('branchId', e.target.value);
  };

  // 관리 담당은 저장 버튼이 없는 카드형 필드라 선택 즉시 저장한다.
  // 회원수정 API는 null 필드를 건너뛰는 부분수정이라 branchId+trainerId만 보내면 담당자만 갱신된다.
  const onTrainerChange = async e => {
    const nextTrainerId = e.target.value;
    const prevTrainerId = userTrainerId;
    setUserTrainerId(nextTrainerId);
    setValue('trainerId', nextTrainerId);

    const branchId = userBranchId ?? user?.branch?.branchId;
    if (!branchId) {
      toast.error(t('지점 정보를 확인할 수 없습니다'));
      return;
    }
    try {
      const formData = converterJsonToFormData(
        removeEmptyValues({ branchId, trainerId: nextTrainerId }),
      );
      await updateUserById(formData, props.user.memberId);
      toast.success(t('관리 담당자가 변경되었습니다'));
    } catch (error) {
      setUserTrainerId(prevTrainerId);
      setValue('trainerId', prevTrainerId);
      toast.error(errorMessages(error));
    }
  };

  const submit = async values => {
    if (values.password !== values.verifyPassword) {
      return setError('verifyPassword', {
        type: 'custom',
        message: t('Password not matched'),
      });
    }

    if (values.birthDate) {
      const formattedDate = moment(values.birthDate).format('YYYY-MM-DD');
      values.birthDate = formattedDate;
    }

    // 지점 등록일(백로그 #170) — 서버는 화면에 보이던 값과 다를 때만 저장한다
    if (values.branchRegisteredDate) {
      values.branchRegisteredDate = moment(values.branchRegisteredDate).format('YYYY-MM-DD');
    }

    // 전화번호 하이픈 포맷팅 (010-1234-5678 형식)
    if (values.phoneNumber) {
      const digits = values.phoneNumber.replaceAll('-', '');
      if (digits.length === 11) {
        values.phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
      } else if (digits.length === 10) {
        values.phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
    }

    // profileImage, authImage 제외. memo도 제외 — 전용 메모 API가 단일 저장 경로.
    const { profileImage, authImage, memo, ...valuesWithoutImage } = values;

    const formData = converterJsonToFormData(
      removeEmptyValues({
        ...valuesWithoutImage,
      }),
    );

    try {
      await updateUserById(formData, props.user.memberId);
      toast.success(t('Member information has been modified'));
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  function deleteUser() {
    deleteUserById(props.user.id, user.token)
      .then(() => {
        navigate('/member');
      })
      .catch(error => {
        toast.error(errorMessages(error));
      });
  }

  useEffect(() => {
    if (props.user.id) {
      reset(props.user);
      if (user.userPoint) {
        setValue('pointID', user.userPoint.pointId);
      }
    }
  }, [props.user.id]);

  // 메모는 전용 API(PUT {id}/memo, 헤더의 메모 모달)가 담당하므로 이 폼에 싣지 않는다.
  useEffect(() => {
    if (userInfo) {
      setUserTrainerId(userInfo?.representativeTrainer);
    }
  }, [userInfo]);

  // 부모(UserDetail)에서 lifting된 userBranchId를 form 평탄 필드로 동기화.
  // ad7fb9b 디자인 리뉴얼에서 마운트 시 setValue('branchId', ...) 호출이 누락되어,
  // 사용자가 지점 드롭다운을 건드리지 않으면 BE의 MemberUpdateRequest.branchId == null이 되어
  // 메모 같은 분기가 저장되지 않던 회귀를 복원.
  useEffect(() => {
    if (userBranchId != null) setValue('branchId', userBranchId);
  }, [userBranchId, setValue]);

  useEffect(() => {
    const birthDate = watch('birthDate');
    if (birthDate) {
      const formattedDate = moment(birthDate).format('YYYY-MM-DD'); // Format birthDate

      if (formattedDate.length === 10) {
        const age = moment().diff(moment(formattedDate, 'YYYY-MM-DD'), 'years'); // Calculate age

        setValue('age', age);
      } else {
        setValue('age', '');
      }
    } else {
      setValue('age', '');
    }
  }, [watch('birthDate')]);

  const password = watch('password');

  return (
    <PageContainer>
      <UserProfileSection
        t={t}
        user={props.user}
        bigUser={props.bigUser}
        control={control}
        previewImage={previewImage}
        userInfo={userInfo}
        userBranchId={userBranchId}
        userTrainerId={userTrainerId}
        onPointChange={onPointChange}
        onTrainerChange={onTrainerChange}
        canInfoEdit={canInfoEdit}
        onOpenDefaultModal={() => setOpenDefaultModal(true)}
        onOpenGuardianModal={() => setOpenGuardianModal(true)}
        onOpenPointModal={() => setOpenPointModal(true)}
        onMoreConsultations={() => props.setSelectedTab('six')}
      />
      <hr style={{ border: '1px solid #e1e1e1' }} />
      <UserUsageTables
        t={t}
        userInfo={userInfo}
        memberId={memberId}
        branchId={userBranchId}
        token={props.bigUser?.token}
        onMoreReservations={() => props.setSelectedTab('two', 1)}
        onMoreTickets={() => props.setSelectedTab('three', 0)}
        onMoreStops={() => props.setSelectedTab('eight')}
      />

      <PointChangeModal
        open={openPointModal}
        onClose={() => setOpenPointModal(false)}
        memberId={memberId}
        branchId={userBranchId}
        token={props.bigUser?.token}
        balance={userInfo?.holdingPoint || 0}
      />

      <UserDefaultModal
        open={openDefaultModal}
        onClose={() => setOpenDefaultModal(false)}
        onSave={handleSubmit(submit)}
        control={control}
        setValue={setValue}
        watchedFields={watchedFields}
        password={password}
        user={props.user}
        address={address}
        handleClick={handleClick}
      />
      {/* 보호자 등록 모달 */}
      <GuardianRegisterModal open={openGuardianModal} onClose={() => setOpenGuardianModal(false)} memberId={memberId} />

      <BannerDeleteModal open={opens} setOpen={setOpens} handleDelete={deleteUser} />
    </PageContainer>
  );
};

export default UserGeneralTab;
